import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { DownOutlined } from '@ant-design/icons';
import './customTimePicker.scss';
import timeIconImage from '../../../assets/images/products/time_icon.png'

const CustomTimePicker = forwardRef(({
    value,
    onChange,
    placeholder = "Select time",
    disabled = false,
    required = false,
    className = "",
    format = "12",
    clearIcon = true,
    clockIcon = true,
    ...props
}, ref) => {
    const [timeValue, setTimeValue] = useState(value || '');
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Expose methods to parent components (for Ant Design Form compatibility)
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        },
        blur: () => {
            setIsFocused(false);
            setIsOpen(false);
        }
    }));

    // Generate time options (every 15 minutes)
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const hour24 = hour;
                const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const ampm = hour < 12 ? 'AM' : 'PM';
                const minuteStr = minute.toString().padStart(2, '0');
                
                const displayTime = format === "12" 
                    ? `${hour12}:${minuteStr} ${ampm}`
                    : `${hour24.toString().padStart(2, '0')}:${minuteStr}`;
                
                options.push({
                    value: displayTime,
                    label: displayTime,
                    hour24: hour24,
                    minute: minute
                });
            }
        }
        return options;
    };

    const timeOptions = generateTimeOptions();

    // Update internal state when external value changes
    useEffect(() => {
        setTimeValue(value || '');
    }, [value]);

    // Handle clicking outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle time selection
    const handleTimeSelect = (selectedTime) => {
        setTimeValue(selectedTime);
        setIsOpen(false);
        setIsFocused(false);
        
        if (onChange) {
            onChange(selectedTime);
        }
    };

    // Handle input click
    const handleInputClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            setIsFocused(true);
        }
    };

    // Handle clear time
    const handleClear = (e) => {
        e.stopPropagation();
        setTimeValue('');
        setIsOpen(false);
        if (onChange) {
            onChange('');
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (disabled) return;

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                setIsOpen(!isOpen);
                setIsFocused(true);
                break;
            case 'Escape':
                setIsOpen(false);
                setIsFocused(false);
                break;
            case 'Tab':
                setIsOpen(false);
                setIsFocused(false);
                break;
            default:
                break;
        }
    };

    const containerClasses = [
        'custom-time-picker-container',
        'dropdown-style',
        className,
        disabled ? 'disabled' : '',
        isFocused ? 'focused' : '',
        isOpen ? 'open' : '',
        required ? 'required' : ''
    ].filter(Boolean).join(' ');

    return (
        <div className={containerClasses} ref={dropdownRef}>
            <div 
                className="time-picker-input-wrapper"
                onClick={handleInputClick}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label="Time picker"
            >
                <input
                    ref={inputRef}
                    type="text"
                    className="time-picker-input"
                    value={timeValue || ''}
                    placeholder={placeholder}
                    readOnly
                    disabled={disabled}
                    tabIndex={-1}
                />
                
                <div className="time-picker-icons">
                    {clearIcon && timeValue && (
                        <button
                            type="button"
                            className="clear-button"
                            onClick={handleClear}
                            aria-label="Clear time"
                        >
                            ×
                        </button>
                    )}
                    {clockIcon && (
                        <img src={timeIconImage} alt="Time" className="clock-icon" />
                    )}
                    <DownOutlined className={`dropdown-arrow ${isOpen ? 'open' : ''}`} />
                </div>
            </div>

            {/* Dropdown menu */}
            {isOpen && (
                <div className="time-picker-dropdown" role="listbox">
                    <div className="time-options-container">
                        {timeOptions.map((option, index) => (
                            <div
                                key={index}
                                className={`time-option ${timeValue === option.value ? 'selected' : ''}`}
                                onClick={() => handleTimeSelect(option.value)}
                                role="option"
                                aria-selected={timeValue === option.value}
                                tabIndex={-1}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Enhanced visual feedback */}
            <div className="time-picker-underline"></div>
            
            {/* Optional helper text */}
            {required && !timeValue && (
                <div className="time-picker-helper-text">
                    This field is required
                </div>
            )}
        </div>
    );
});

// Add display name for debugging
CustomTimePicker.displayName = 'CustomTimePicker';

export default CustomTimePicker;
